import React from 'react';

import { Card, Checkbox, Flex, Form, Progress, Spin } from 'antd-v5';
import cn from 'classnames';
import type { DateTime } from 'luxon';
import {
  MdInfo,
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, DatePicker, Heading, StatusTag, Text } from 'scouting-ui';
import { StatusProp } from 'scouting-ui/lib/StatusTag/StatusTag';
import { cssVars } from 'scouting-ui/lib/variables';

import AdvancementAuditTooltip, {
  hasAuditInfo,
} from '@shared/components/AdvancementAudit/AdvancementAuditTooltip';
import AdvancementImage from '@shared/components/AdvancementImage/AdvancementImage';
import { luxonViewDateFormat } from '@shared/constants';
import { intl } from '@shared/localization';
import {
  statusColor,
  tagStatus,
  toPercent,
} from '@shared/utils/advancementDisplay';
import { isAfterToday } from '@utils/datesLuxon';

import styles from './AdvancementDetails.module.scss';
import { AdvancementAction, FormField } from './types';
import type {
  AdvSummaryFormData,
  AdvancementDetailsProps,
  SupportedAdvancementType,
} from './types';

/** Checkbox controls in display order, paired with their `Form.Item` name */
const CHECKBOX_ACTIONS: { action: AdvancementAction; name: FormField }[] = [
  {
    action: AdvancementAction.CounselorApprove,
    name: FormField.CounselorApproved,
  },
  { action: AdvancementAction.Approve, name: FormField.Approved },
  { action: AdvancementAction.Award, name: FormField.Awarded },
];

/** Every control relevant to the type; counselor-approve is merit-badge only */
export const getDefaultVisibleActions = (
  type: SupportedAdvancementType,
): AdvancementAction[] => {
  const actions: AdvancementAction[] = [AdvancementAction.CompletionDate];
  if (type === 'meritBadges') actions.push(AdvancementAction.CounselorApprove);
  actions.push(AdvancementAction.Approve, AdvancementAction.Award);
  return actions;
};

/** Presentation-only summary card; renders `Form.Item` controls into an ancestor `<Form>`
 * DO not put here business logic, it should be up to the user of this component
 * */
export default function AdvancementDetails(props: AdvancementDetailsProps) {
  const {
    type,
    advancement,
    loading,
    saving,
    busy,
    visibleActions,
    disabledActions = [],
    navigation,
    extraContent,
  } = props;
  const form = Form.useFormInstance<AdvSummaryFormData>();

  const { name, imageUrl100, status, percentCompleted } = advancement;
  const tag = tagStatus(status);
  const color = statusColor(tag);
  const percent = toPercent(percentCompleted);
  const hasAudit = hasAuditInfo(advancement);

  const visible = visibleActions ?? getDefaultVisibleActions(type);
  const isVisible = (action: AdvancementAction) => visible.includes(action);
  const isDisabled = (action: AdvancementAction) =>
    disabledActions.includes(action);
  const hasEnabledAction = visible.some(action => !isDisabled(action));

  // `busy` is a transient overlay on top of the policy answer, kept apart from it
  // so a passing refetch cannot drop the Save button and reflow the card
  const isLocked = (action: AdvancementAction) =>
    isDisabled(action) || Boolean(busy);

  // Leader approve/award cascade: checking award implies leader approval, unchecking
  // approve clears award. Counselor approval is independent and only ever set explicitly
  const handleCheckboxChange = (
    action: AdvancementAction,
    checked: boolean,
  ) => {
    if (action === AdvancementAction.Award) {
      form.setFieldsValue({
        [FormField.Awarded]: checked,
        ...(checked ? { [FormField.Approved]: true } : {}),
      });
    } else if (action === AdvancementAction.Approve) {
      form.setFieldsValue({
        [FormField.Approved]: checked,
        ...(checked ? {} : { [FormField.Awarded]: false }),
      });
    } else if (action === AdvancementAction.CounselorApprove) {
      form.setFieldsValue({ [FormField.CounselorApproved]: checked });
    }
  };

  return (
    <Card className={styles.card} loading={loading}>
      {/* Spin only dims its children, so `busy` also greys every control below */}
      <Spin spinning={Boolean(busy)}>
        <Flex vertical align="center" gap="small" className={styles.content}>
          <Flex
            align="flex-end"
            justify="center"
            gap={32}
            className={styles.nav}
          >
            {navigation && (
              <Flex vertical align="center" gap={4} className={styles.navSide}>
                <Button
                  type={'default'}
                  shape="default"
                  variant="filled"
                  icon={<MdKeyboardArrowLeft />}
                  disabled={!navigation.hasPrev}
                  onClick={navigation.onPrev}
                  aria-label={navigation.prevLabel}
                />
                {/* Always rendered so both sides keep equal height and the arrows stay level */}
                <div className={styles.navLabel}>
                  {navigation.hasPrev && (
                    <>
                      <Text font="buttons-small" color="color-info">
                        <FormattedMessage id="advancementDetails.nav.previous" />
                      </Text>
                      <Text font="body-caption" color="color-text-light">
                        {navigation.prevLabel}
                      </Text>
                    </>
                  )}
                </div>
              </Flex>
            )}
            <AdvancementImage
              src={imageUrl100 ?? undefined}
              className={styles.image}
            />
            {navigation && (
              <Flex vertical align="center" gap={4} className={styles.navSide}>
                <Button
                  type={'default'}
                  shape="default"
                  variant="filled"
                  icon={<MdKeyboardArrowRight />}
                  disabled={!navigation.hasNext}
                  onClick={navigation.onNext}
                  aria-label={navigation.nextLabel}
                />
                <div className={styles.navLabel}>
                  {navigation.hasNext && (
                    <>
                      <Text font="buttons-small" color="color-info">
                        <FormattedMessage id="advancementDetails.nav.next" />
                      </Text>
                      <Text font="body-caption" color="color-text-light">
                        {navigation.nextLabel}
                      </Text>
                    </>
                  )}
                </div>
              </Flex>
            )}
          </Flex>

          <Text font="body-overline" color="color-text-medium">
            <FormattedMessage id={`advancementDetails.type.${type}`} />
          </Text>
          <Heading
            font="display-h4"
            color="color-text-regular"
            className={styles.name}
          >
            {name}
          </Heading>

          {navigation?.steps && navigation.steps.length > 1 && (
            <Flex gap={8} justify="center" wrap className={styles.steps}>
              {navigation.steps.map(step => (
                <button
                  key={step.id}
                  type="button"
                  className={cn(styles.dot, {
                    [styles.dotActive]: step.active,
                  })}
                  aria-label={step.name}
                  aria-current={step.active}
                  onClick={() => navigation.onSelectStep?.(step.id)}
                />
              ))}
            </Flex>
          )}

          {percent < 100 && (
            <Text font="body-caption" color={color}>
              {`${percent}% `}
              <FormattedMessage id="advancementDetails.progress" />
            </Text>
          )}
          <Progress
            className={styles.progress}
            percent={percent}
            strokeColor={cssVars[color]}
            strokeLinecap="round"
            showInfo={false}
            size={['100%', 10]}
          />

          {/* Tag shows once started; the info icon only when there are sign-offs to audit */}
          {tag !== StatusProp.NOT_STARTED && (
            <AdvancementAuditTooltip advancement={advancement}>
              <span
                className={cn(styles.status, {
                  [styles.hasAudit]: hasAudit,
                })}
              >
                <StatusTag status={tag} />
                {hasAudit && (
                  <MdInfo
                    className={styles.infoIcon}
                    color={cssVars['color-info']}
                  />
                )}
              </span>
            </AdvancementAuditTooltip>
          )}

          <Flex vertical align="center" gap="small" className={styles.actions}>
            {isVisible(AdvancementAction.CompletionDate) && (
              <Form.Item
                name={FormField.CompletionDate}
                className={styles.dateItem}
                label={
                  <Text font="body-caption" strong>
                    <FormattedMessage id="advancementDetails.completionDate" />
                  </Text>
                }
                // the rule backs up `disabledDate`, which cannot police a value the
                // record already carried when the form mounted
                rules={[
                  {
                    validator: (_rule, value: DateTime | null) =>
                      !value || !isAfterToday(value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: 'advancementDetails.futureDate',
                              }),
                            ),
                          ),
                  },
                ]}
              >
                <DatePicker
                  placeholder={intl.formatMessage({
                    id: 'advancementDetails.completionDate',
                  })}
                  className={styles.datePicker}
                  format={luxonViewDateFormat}
                  disabled={isLocked(AdvancementAction.CompletionDate)}
                  disabledDate={isAfterToday}
                />
              </Form.Item>
            )}

            <Flex vertical align="flex-start" className={styles.checkGroup}>
              {CHECKBOX_ACTIONS.filter(({ action }) => isVisible(action)).map(
                ({ action, name: fieldName }) => (
                  <Form.Item
                    key={action}
                    name={fieldName}
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox
                      disabled={isLocked(action)}
                      onChange={e =>
                        handleCheckboxChange(action, e.target.checked)
                      }
                    >
                      <FormattedMessage
                        id={`advancementDetails.action.${action}`}
                      />
                    </Checkbox>
                  </Form.Item>
                ),
              )}
            </Flex>

            {hasEnabledAction && (
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                disabled={Boolean(busy)}
                className={styles.save}
              >
                <FormattedMessage id="advancementDetails.save" />
              </Button>
            )}

            {extraContent}
          </Flex>
        </Flex>
      </Spin>
    </Card>
  );
}
